import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Grid} from "@mui/material";
import moment from "moment";
import {getSupplierSpaceUsageByPage} from "../requests/reports/space-usage/supplier-space-usage-requests";
import {DateFormats} from "../components/constants/securspace-constants";
import {getErrorMessage} from "../util/NetworkErrorUtil";
import SpaceUsageItemsPaginated from "../components/spaceUsage/SpaceUsageItemsPaginated";
import SpaceUsageLoader from "../components/deprecatedUikit/SpaceUsageCard/SpaceUsageLoader";
import NoMatchingInvoicesFound from "../components/NoMatchingInvoicesFound";
import BookedSpaceCalendarInvoiceModal from "../components/bookedSpaces/BookedSpaceCalendarInvoiceModal";
import SpaceUsageFilterWithQueryParams from "../components/spaceUsage/supplier/SpaceUsageFilterWithQueryParams";
import useUrlQueryParams from "../hooks/useUrlQueryParams";
import SpaceUsageExportCSVs from "../components/spaceUsage/supplier/SpaceUsageExportCSVs";
import SecurSpaceContainer from '../components/common/SecurSpaceContainer';
import ReportDataSummary from '../components/report/redesigned/ReportDataSummary';
import NoDataMessage from "../components/deprecatedUikit/NoDataMessage/NoDataMessage";
import {SecurSpaceReportContainer, SecurSpaceReportTitle} from "../components/common";
import {useSnackbar} from "../context/snackbar-context";

const SupplierSpaceUsage = () => {
  const {snackbarShowMessage} = useSnackbar();

  const {overageInvoiceNumber} = useUrlQueryParams();
  const lastMonthDate = moment().add(-1, 'months').format(DateFormats.DAY).toString();
  const todayDate = moment().format(DateFormats.DAY).toString();
  const [loading, setLoading] = useState(false)

  const [query, setQuery] = useState({
    locationId: '',
    bookingNumber: '',
    overageInvoiceNumber: overageInvoiceNumber || '',
    startDate: lastMonthDate,
    endDate: todayDate,
    sortBy: 'CALENDAR_DATE',
    sortDir: 'asc'
  });
  const [locationName, setLocationName] = useState('ALL');
  const [spaceUsageItems, setSpaceUsageItems] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [formChanged, setFormChanged] = useState(false);
  const [showOverageInvoicesModal, setShowOverageInvoicesModal] = useState(false);
  const [overageInvoicesToView, setOverageInvoicesToView] = useState([]);

  const initialRender = useRef(true);

  useEffect(() => {
    const {locationId, bookingNumber, overageInvoiceNumber, startDate, endDate, sortBy, sortDir} = query;
    let ignore = false;
    setLoading(true);
    getSupplierSpaceUsageByPage({
      locationId,
      bookingNumber,
      overageInvoiceNumber,
      startDate,
      endDate,
      sortBy,
      sortDir,
      page,
      size
    }).then((response) => {
      if (!ignore && response.body) {
        const {content, totalPages, count, page} = response.body;
        if (page + 1 > totalPages) {
          // If the page index is greater than the total pages
          // This will refresh the data and show the last page of results
          setPage(totalPages > 0 ? totalPages - 1 : 0);
        }
        setSpaceUsageItems(content);
        setTotalElements(count);
      }
    })
      .catch((error) => {
        snackbarShowMessage(getErrorMessage(error), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [query, page, size, snackbarShowMessage]);

  const handleChange = useCallback((event) => {
    if (!initialRender.current) {
      const bookingNumber = event.filters.find((filter) => filter.name === 'bookingNumber')?.value;
      const overageInvoiceNumber = event.filters.find((filter) => filter.name === 'overageInvoiceNumber')?.value;
      setQuery({
        locationId: event.location.id,
        bookingNumber: bookingNumber,
        overageInvoiceNumber: overageInvoiceNumber,
        startDate: event.startDate,
        endDate: event.endDate,
        sortBy: event.sortBy,
        sortDir: event.sortDir
      });
      setLocationName(event.location.locationName);
      setFormChanged(true);
    }
    else {
      initialRender.current = false;
  }
  }, []);

  const handlePageChange = (_, page) => {
    setPage(page);
  };

  const handleRowsPerPageChange = (event) => {
    setSize(parseInt(event.target.value, 10));
  };

  const handleOpenOverageInvoicesModal = (_, overageInvoices) => {
    setOverageInvoicesToView(overageInvoices);
    setShowOverageInvoicesModal(true);
  };

  const closeOverageInvoicesModal = () => {
    setOverageInvoicesToView([]);
    setShowOverageInvoicesModal(false);
  };

  return (
    <SecurSpaceContainer>
      <SecurSpaceReportContainer>
        <Grid item mb={2}>
          <SecurSpaceReportTitle>Space Usage</SecurSpaceReportTitle>
        </Grid>
        <BookedSpaceCalendarInvoiceModal
          isOpen={showOverageInvoicesModal}
          closeModal={closeOverageInvoicesModal}
          invoiceItems={overageInvoicesToView}
        />
        <SpaceUsageFilterWithQueryParams
          onChange={handleChange}
          defaultStartDate={lastMonthDate}
          defaultEndDate={todayDate}
        />
        {
          !loading && spaceUsageItems.length > 0 &&
          <ReportDataSummary
            count={spaceUsageItems.length}
            title={locationName}
          >
            <SpaceUsageExportCSVs
              query={query}
            />
          </ReportDataSummary>
        }
        {
        loading ? Array.from({ length: 12 }).map((_, index) => (
          <Grid item sx={{ width:'100%' }} key={index}>
            <SpaceUsageLoader />
          </Grid>
      )) : spaceUsageItems.length ? <SpaceUsageItemsPaginated
            spaceUsageItems={spaceUsageItems}
            count={totalElements}
            rowsPerPage={size}
            onRowsPerPageChange={handleRowsPerPageChange}
            page={page}
            onPageChange={handlePageChange}
            onViewOverageInvoices={handleOpenOverageInvoicesModal}
          /> : formChanged ? <NoMatchingInvoicesFound item message="No matching records found" /> :
            <Grid item container><NoDataMessage textStyle={""}/></Grid>
        }
      </SecurSpaceReportContainer>
    </SecurSpaceContainer>
  );
};

export default SupplierSpaceUsage;
